import { Link } from "react-router-dom";
import { ArrowRight, Phone, Home, Shield, FileCheck, CheckCircle2, Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80";
const ABOUT_IMAGE = "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=800&q=80";
const WHY_IMAGE = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80";

const testimonials = [
  {
    name: "Emily R",
    text: "KK Mortgage Solutions provided top-notch service from start to finish. They took the time to understand my financial needs and found the perfect mortgage solution for me. Their team was always available to answer questions, and I felt confident throughout the entire process. Highly recommended!",
  },
  {
    name: "Rakesh Patel",
    text: "I had an amazing experience with KK Mortgage Solutions. The team was knowledgeable, patient, and truly committed to finding the best mortgage solution for my needs. They walked me through every step of the process, answering all my questions and ensuring I felt confident in my decisions.",
  },
  {
    name: "Sophia L",
    text: "KK Mortgage Solutions made my home-buying journey seamless by offering both mortgage and home insurance services under one roof. Their team coordinated everything perfectly, saving me time and stress. The mortgage process was smooth, and their insurance plan offered great value.",
  },
  {
    name: "Sarah Thompson",
    text: "The professionalism and expertise of KK Mortgage Solutions are unmatched. They took the time to explain all my options and found a mortgage plan tailored to my needs. The process was smooth, and their attention to detail gave me great confidence in their services.",
  },
  {
    name: "Michael Brown",
    text: "KK Mortgage Solutions made refinancing my home simple and hassle-free. They were transparent, quick to respond to my questions, and found the best rates for me. Their friendly and professional approach made the entire process enjoyable.",
  },
  {
    name: "David Ramirez",
    text: "KK Mortgage Solutions truly exceeded my expectations! Their personalized approach and dedication to finding the best mortgage option for my family were outstanding. They explained everything in detail, ensuring I understood every step of the process.",
  },
];

const services = [
  {
    icon: Home,
    title: "Mortgage",
    desc: "We know it can be confusing and difficult to understand the mortgage marketplace. Let us take care of it for you.",
    link: "/services",
  },
  {
    icon: Shield,
    title: "Home Insurance",
    desc: "Home insurance protects your home and its belongings from fire, storm, flood, theft and more.",
    link: "/services",
  },
  {
    icon: FileCheck,
    title: "Protection",
    desc: "We only work with providers that we trust, with an established history of paying out and excellent communication.",
    link: "/services",
  },
];

const whyChooseUs = [
  "Tailored Solutions for Every Client",
  "Experienced and Trusted Advisors",
  "Wide Range of Lender Access",
  "Dedicated Client Support",
];

export default function HomePage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () =>
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () =>
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(17, 17, 17, 0.92) 0%, rgba(17, 17, 17, 0.78) 100%)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-4 font-body opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Your Trusted Mortgage Partner
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Your Key to the
              <span className="text-[#d64241]"> Perfect Mortgage</span>
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-10 max-w-lg opacity-0 animate-fade-in-up font-body" style={{ animationDelay: '0.3s' }}>
              We specialize in helping individuals and families navigate the home financing process{" "}
              <strong className="text-white">with confidence and ease</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <Link to="/contact">
                <Button
                  data-testid="hero-cta-btn"
                  className="bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
                >
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <a href="tel:07538962618">
                <Button
                  data-testid="hero-call-btn"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base font-semibold border-2 border-white/30 text-white hover:bg-white/10 hover:text-white transition-all duration-200"
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section data-testid="about-preview-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
                About Us
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black tracking-tight mb-6">
                Your Partner in Achieving Homeownership Dreams
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 font-body">
                At KK Mortgage Solutions, we specialize in helping individuals and families navigate
                the home financing process with confidence and ease. As a trusted mortgage brokerage,
                we work closely with a network of reputable lenders to find the best loan options
                tailored to your unique needs and goals.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 font-body">
                With a commitment to transparency, reliability, and exceptional customer service, we
                take pride in being more than just a mortgage broker—we're your partner in achieving
                your homeownership dreams.
              </p>
              <Link to="/about">
                <Button
                  data-testid="about-preview-btn"
                  className="bg-black hover:bg-[#1a1a1a] text-white rounded-full px-8 py-6 text-base font-medium hover:-translate-y-0.5 transition-all duration-200"
                >
                  About Us
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
                <img
                  src={ABOUT_IMAGE}
                  alt="Family with new home"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#d64241] text-white rounded-2xl p-6 shadow-xl hidden lg:block">
                <p className="text-3xl font-bold font-heading">10+</p>
                <p className="text-sm font-body text-red-100">Years of Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
              Services
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black tracking-tight">
              Helping You Achieve Your Goals
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, i) => (
              <Link
                key={service.title}
                to={service.link}
                data-testid={`service-card-${service.title.toLowerCase().replace(' ', '-')}`}
                className="service-card bg-white p-8 lg:p-10 rounded-2xl border border-gray-100 group cursor-pointer"
              >
                <div className="w-14 h-14 rounded-xl bg-[#fef2f2] flex items-center justify-center mb-6 group-hover:bg-[#d64241] transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-[#d64241] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-black mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed font-body text-sm">{service.desc}</p>
                <div className="mt-6 flex items-center text-[#d64241] font-medium text-sm font-body group-hover:translate-x-1 transition-transform duration-200">
                  Learn More <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
              Testimonials
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black tracking-tight">
              What People Say About Us
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="testimonial-card bg-gray-50 rounded-2xl p-8 lg:p-12 relative border border-gray-100">
              <Quote className="w-10 h-10 text-[#f4adad] mb-6" />
              <p className="text-lg text-gray-700 leading-relaxed mb-8 font-body italic">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#d64241] flex items-center justify-center text-white font-bold font-heading text-lg">
                    {testimonials[currentTestimonial].name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-black font-body">
                      {testimonials[currentTestimonial].name}
                    </p>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#d64241] text-[#d64241]" />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="testimonial-prev-btn"
                    onClick={prevTestimonial}
                    className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-black hover:text-white hover:border-black transition-all duration-200"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    data-testid="testimonial-next-btn"
                    onClick={nextTestimonial}
                    className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-black hover:text-white hover:border-black transition-all duration-200"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentTestimonial(i)}
                  data-testid={`testimonial-dot-${i}`}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                    i === currentTestimonial ? "bg-[#d64241] w-8" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
                <img
                  src={WHY_IMAGE}
                  alt="Modern home"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
                Why Choose Us
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black tracking-tight mb-6">
                More Than Just a Mortgage Broker
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 font-body">
                Our mission is to simplify the complexities of home financing, providing
                personalized guidance every step of the way. Whether you're buying your first home,
                refinancing, or investing in property, our team is here to ensure you get the best
                rates, terms, and solutions available.
              </p>
              <div className="space-y-4">
                {whyChooseUs.map((item, i) => (
                  <div
                    key={i}
                    data-testid={`why-item-${i}`}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#d64241] flex-shrink-0" />
                    <span className="text-gray-800 font-medium font-body">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="relative py-20 lg:py-24">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80)`,
          }}
        />
        <div className="absolute inset-0 bg-black/90" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            Ready to Unlock Your Dream Home?
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto font-body">
            Contact us today to start your journey towards homeownership. Our expert team is ready
            to help you find the perfect mortgage solution.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-contact-btn"
                className="bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <a href="tel:07538962618">
              <Button
                data-testid="cta-call-btn"
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-semibold border-2 border-white/30 text-white hover:bg-white/10 hover:text-white transition-all duration-200"
              >
                <Phone className="w-4 h-4 mr-1" />
                07538962618
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
